import React, { useEffect, useRef, useState } from "react";

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-20"
            }`}
          >
            <div className="relative h-[600px] rounded-3xl overflow-hidden shadow-2xl card-premium">
              <img
                src="/assets/images/default.jpg"
                alt="About Us"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
              <div className="absolute bottom-12 left-12 text-white">
                <p className="text-6xl font-bold mb-2">Sejak 2017</p>
                <p className="text-2xl font-light">Melayani dengan Dedikasi</p>
              </div>
            </div>
          </div>

          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-20"
            }`}
          >
            <h2 className="text-5xl pb-4 lg:text-6xl font-bold text-gradient mb-8">
              Tentang Kami
            </h2>
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              PT Berkat Mandiri Expedisi adalah perusahaan yang bergerak di
              bidang jasa expedisi dengan pengalaman sejak tahun 2017.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Kami menyediakan berbagai layanan pengiriman seperti Pengiriman
              Darat, Kargo Besar, Barang Fragile dan Berharga serta Pengiriman
              Rutin dan Massal dengan cakupan seluruh Pulau Jawa.
            </p>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Dengan jaringan luas dan tim profesional, kami berkomitmen
              menyediakan layanan pengiriman yang cepat, aman dan handal.
            </p>

            <div className="space-y-8">
              {/* Vision */}
              <div className="glass-dark rounded-3xl p-8 card-premium border border-primary/20">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0 glow">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-primary mb-3 flex items-center">
                      Visi Kami
                      <span className="ml-3 text-sm font-normal text-gray-500 italic">
                        Our Vision
                      </span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Menjadi perusahaan expedisi yang dapat dipercaya dan
                      memberikan rasa ketenangan bagi yang menggunakan jasa
                      kami.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mission */}
              <div className="glass-dark rounded-3xl p-8 card-premium border border-primary/20">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0 glow">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-primary mb-4 flex items-center">
                      Misi Kami
                      <span className="ml-3 text-sm font-normal text-gray-500 italic">
                        Our Mission
                      </span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3 group">
                        <svg
                          className="w-6 h-6 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-gray-700 leading-relaxed">
                          Memberikan pelayanan terbaik dan terjamin
                        </p>
                      </div>
                      <div className="flex items-start space-x-3 group">
                        <svg
                          className="w-6 h-6 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-gray-700 leading-relaxed">
                          Menjamin layanan pengiriman yang tepat waktu, aman dan
                          sesuai pesanan
                        </p>
                      </div>
                      <div className="flex items-start space-x-3 group">
                        <svg
                          className="w-6 h-6 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        <p className="text-gray-700 leading-relaxed">
                          Memberikan rasa keamanan dan ketenangan kepada
                          customer dalam setiap pengiriman
                        </p>
                      </div>
                      <div className="flex items-start space-x-3 group">
                        <svg
                          className="w-6 h-6 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                        <p className="text-gray-700 leading-relaxed">
                          Meningkatkan kualitas layanan melalui inovasi dan
                          penerapan teknologi terbaru
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
